import { useRef, useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaintBrush,
  faEraser,
  faUndo,
  faTrash,
  faPaperPlane,
  faMagnifyingGlassPlus,
  faMagnifyingGlassMinus,
  faExpand,
  faHand,
} from '@fortawesome/free-solid-svg-icons';
import './SketcherCanvas.css';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;

interface Props {
  sketchItem: string;
  hint: string;
  roundNumber: number;
  totalRounds: number;
  timeLeft: number;
  totalTime: number;
  onSubmit: (imageBase64: string, isTimeout: boolean) => void;
  submitted: boolean;
}

const COLORS = [
  '#1B2A4A',
  '#E74C3C',
  '#F5A623',
  '#27AE60',
  '#3B82F6',
  '#AB47BC',
  '#FF7043',
  '#4ECDC4',
  '#F9A825',
  '#8B4513',
  '#FFFFFF',
  '#8E95A9',
];

const BRUSH_SIZES = [4, 8, 14, 22];

const SketcherCanvas: React.FC<Props> = ({
  sketchItem,
  hint,
  roundNumber,
  totalRounds,
  timeLeft,
  totalTime,
  onSubmit,
  submitted,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState('#1B2A4A');
  const [brushSize, setBrushSize] = useState(8);
  const [tool, setTool] = useState<'brush' | 'eraser' | 'pan'>('brush');
  const [history, setHistory] = useState<ImageData[]>([]);
  const lastPos = useRef<{ x: number; y: number } | null>(null);

  // Zoom & pan view state. These only affect how the canvas is displayed —
  // the canvas internal resolution stays fixed so submitted image quality is
  // unchanged. `pan` is expressed in screen (CSS) pixels.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const panStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const [spaceHeld, setSpaceHeld] = useState(false);
  // Pinch-to-zoom tracking
  const pinchRef = useRef<{ dist: number; zoom: number } | null>(null);

  // On-screen zoom hint — shown until the user first interacts with zoom
  const [showHint, setShowHint] = useState(true);
  const isTouchDevice =
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  // ----- Zoom & pan helpers (declared before effects that reference them) -----
  const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

  // Clamp pan so the (scaled) canvas can't be dragged completely out of view.
  const clampPan = useCallback(
    (p: { x: number; y: number }, z: number) => {
      const wrap = wrapRef.current;
      if (!wrap) return p;
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      // Max offset keeps the scaled content overlapping the viewport.
      const maxX = (w * (z - 1)) / 2;
      const maxY = (h * (z - 1)) / 2;
      return {
        x: Math.min(maxX, Math.max(-maxX, p.x)),
        y: Math.min(maxY, Math.max(-maxY, p.y)),
      };
    },
    []
  );

  const applyZoom = useCallback(
    (nextZoom: number) => {
      setShowHint(false);
      setZoom(() => {
        const z = clampZoom(nextZoom);
        setPan((p) => clampPan(z === 1 ? { x: 0, y: 0 } : p, z));
        return z;
      });
    },
    [clampPan]
  );

  const zoomIn = () => applyZoom(zoom + ZOOM_STEP);
  const zoomOut = () => applyZoom(zoom - ZOOM_STEP);
  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Timer ring
  const circumference = 2 * Math.PI * 44;
  const progress = totalTime > 0 ? timeLeft / totalTime : 1;
  const dashOffset = circumference * (1 - progress);

  // Track whether the user has drawn anything on the canvas
  const canvasModifiedRef = useRef(false);
  const [timerExpired, setTimerExpired] = useState(false);
  const autoSubmittedRef = useRef(false);

  // Determine if canvas is locked (timer expired or already submitted)
  const isLocked = submitted || timerExpired;

  // Detect timer expiry and auto-submit if canvas was modified
  useEffect(() => {
    if (timeLeft <= 0 && totalTime > 0 && !autoSubmittedRef.current) {
      setTimerExpired(true);
      if (canvasModifiedRef.current && !submitted) {
        autoSubmittedRef.current = true;
        const canvas = canvasRef.current;
        if (canvas) {
          const dataUrl = canvas.toDataURL('image/png');
          onSubmit(dataUrl, true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  // Reset state when a new round starts (totalTime changes)
  useEffect(() => {
    canvasModifiedRef.current = false;
    autoSubmittedRef.current = false;
    setTimerExpired(false);
  }, [totalTime]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    saveHistory();
  }, []);

  // Reset zoom/pan at the start of each new round
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [totalTime]);

  // If zoom returns to 1x while the pan tool is active, there's nothing to
  // pan — switch back to the brush so the user isn't stuck in a no-op mode.
  useEffect(() => {
    if (zoom <= MIN_ZOOM && tool === 'pan') {
      setTool('brush');
    }
  }, [zoom, tool]);

  // Hold Space to temporarily switch to pan mode (desktop convenience)
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        setSpaceHeld(true);
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') setSpaceHeld(false);
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  // Ctrl/⌘ + wheel to zoom toward the cursor. Registered as a non-passive
  // native listener so we can preventDefault the browser's page zoom.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      setShowHint(false);
      const rect = wrap.getBoundingClientRect();
      // Cursor position relative to wrapper centre (origin of the transform)
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;

      setZoom((prevZoom) => {
        const factor = e.deltaY < 0 ? 1 + ZOOM_STEP : 1 - ZOOM_STEP;
        const next = clampZoom(prevZoom * factor);
        const ratio = next / prevZoom;
        setPan((prevPan) => {
          if (next === 1) return { x: 0, y: 0 };
          // Keep the point under the cursor stationary while zooming.
          const nx = cx - (cx - prevPan.x) * ratio;
          const ny = cy - (cy - prevPan.y) * ratio;
          return clampPan({ x: nx, y: ny }, next);
        });
        return next;
      });
    };

    wrap.addEventListener('wheel', onWheel, { passive: false });
    return () => wrap.removeEventListener('wheel', onWheel);
  }, [clampPan]);

  const saveHistory = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setHistory((prev) => [...prev.slice(-20), data]);
  };

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    // getBoundingClientRect() already reflects the CSS transform (zoom + pan)
    // applied to the wrapper, so rect.width/height grow with zoom. Mapping
    // client coords through canvas.width / rect.width therefore yields the
    // correct canvas pixel regardless of the current zoom/pan.
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      const touch = e.touches[0];
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    // Two-finger touch => start pinch-zoom / pan, never draw
    if ('touches' in e && e.touches.length === 2) {
      setShowHint(false);
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      pinchRef.current = { dist, zoom };
      panStartRef.current = {
        x: (a.clientX + b.clientX) / 2,
        y: (a.clientY + b.clientY) / 2,
        panX: pan.x,
        panY: pan.y,
      };
      return;
    }

    // Pan when: pan tool selected, Space held, or middle mouse button.
    const isMouse = !('touches' in e);
    const middle = isMouse && (e as React.MouseEvent).button === 1;
    if (tool === 'pan' || ((spaceHeld || middle) && zoom > 1)) {
      isPanningRef.current = true;
      const point = isMouse
        ? { x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY }
        : { x: (e as React.TouchEvent).touches[0].clientX, y: (e as React.TouchEvent).touches[0].clientY };
      panStartRef.current = { x: point.x, y: point.y, panX: pan.x, panY: pan.y };
      return;
    }

    if (isLocked) return;
    setIsDrawing(true);
    const pos = getPos(e);
    lastPos.current = pos;
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    // Pinch-zoom (two active touches)
    if ('touches' in e && e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const next = clampZoom(pinchRef.current.zoom * (dist / pinchRef.current.dist));
      setZoom(next);
      // Pan by movement of the pinch centre
      if (panStartRef.current) {
        const midX = (a.clientX + b.clientX) / 2;
        const midY = (a.clientY + b.clientY) / 2;
        setPan(
          clampPan(
            {
              x: panStartRef.current.panX + (midX - panStartRef.current.x),
              y: panStartRef.current.panY + (midY - panStartRef.current.y),
            },
            next
          )
        );
      }
      return;
    }

    // Panning (space / middle-drag)
    if (isPanningRef.current && panStartRef.current) {
      const isMouse = !('touches' in e);
      const point = isMouse
        ? { x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY }
        : { x: (e as React.TouchEvent).touches[0].clientX, y: (e as React.TouchEvent).touches[0].clientY };
      setPan(
        clampPan(
          {
            x: panStartRef.current.panX + (point.x - panStartRef.current.x),
            y: panStartRef.current.panY + (point.y - panStartRef.current.y),
          },
          zoom
        )
      );
      return;
    }

    if (!isDrawing || isLocked) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx || !lastPos.current) return;

    const pos = getPos(e);

    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = tool === 'eraser' ? '#FFFFFF' : color;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();

    lastPos.current = pos;
  };

  const stopDrawing = () => {
    // End any pan / pinch gesture
    if (isPanningRef.current || pinchRef.current) {
      isPanningRef.current = false;
      pinchRef.current = null;
      panStartRef.current = null;
      return;
    }
    if (isDrawing) {
      setIsDrawing(false);
      lastPos.current = null;
      canvasModifiedRef.current = true;
      saveHistory();
    }
  };

  const handleUndo = () => {
    if (history.length <= 1) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const prev = history.slice(0, -1);
    setHistory(prev);
    if (prev.length > 0) {
      ctx.putImageData(prev[prev.length - 1], 0, 0);
    }
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    saveHistory();
  };

  const handleSubmit = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    onSubmit(dataUrl, false);
  };

  return (
    <div className="skc">
      {/* Top bar */}
      <div className="skc__topbar">
        <div className="skc__round-info">
          <span className="skc__round-label">
            Round {roundNumber}/{totalRounds}
          </span>
          <span className="skc__item-label">
            Draw: <strong>{sketchItem}</strong>
          </span>
        </div>

        <div className="skc__timer-wrap">
          <svg className="skc__timer-svg" viewBox="0 0 100 100">
            <circle className="skc__timer-bg" cx="50" cy="50" r="44" />
            <circle
              className={`skc__timer-ring ${timeLeft <= 5 ? 'skc__timer-ring--expired' : ''}`}
              cx="50"
              cy="50"
              r="44"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
            />
          </svg>
          <span className="skc__timer-text">{timeLeft}</span>
        </div>

        <div className="skc__hint-box">
          <span className="skc__hint-label">💡 Hint</span>
          <span className="skc__hint-text">{hint}</span>
        </div>
      </div>

      {/* Main canvas area */}
      <div className="skc__body">
        {/* Toolbar */}
        <div className="skc__toolbar">
          <button
            className={`skc__tool-btn ${tool === 'brush' ? 'skc__tool-btn--active' : ''}`}
            onClick={() => setTool('brush')}
            title="Brush"
          >
            <FontAwesomeIcon icon={faPaintBrush} />
          </button>
          <button
            className={`skc__tool-btn ${tool === 'eraser' ? 'skc__tool-btn--active' : ''}`}
            onClick={() => setTool('eraser')}
            title="Eraser"
          >
            <FontAwesomeIcon icon={faEraser} />
          </button>
          <div className="skc__divider" />
          {BRUSH_SIZES.map((size) => (
            <button
              key={size}
              className={`skc__size-btn ${brushSize === size ? 'skc__size-btn--active' : ''}`}
              onClick={() => setBrushSize(size)}
              title={`Size ${size}`}
            >
              <span
                className="skc__size-dot"
                style={{ width: Math.min(size, 20), height: Math.min(size, 20) }}
              />
            </button>
          ))}
          <div className="skc__divider" />
          <button className="skc__tool-btn" onClick={handleUndo} title="Undo">
            <FontAwesomeIcon icon={faUndo} />
          </button>
          <button className="skc__tool-btn" onClick={handleClear} title="Clear">
            <FontAwesomeIcon icon={faTrash} />
          </button>
          <div className="skc__divider" />
          <button
            className="skc__tool-btn"
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
            title="Zoom out"
          >
            <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
          </button>
          <span className="skc__zoom-level" title="Current zoom">
            {Math.round(zoom * 100)}%
          </span>
          <button
            className="skc__tool-btn"
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
            title="Zoom in"
          >
            <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
          </button>
          <button
            className="skc__tool-btn"
            onClick={resetZoom}
            disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
            title="Reset zoom"
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
          <button
            className={`skc__tool-btn ${tool === 'pan' ? 'skc__tool-btn--active' : ''}`}
            onClick={() => setTool(tool === 'pan' ? 'brush' : 'pan')}
            disabled={zoom <= MIN_ZOOM}
            title={
              zoom <= MIN_ZOOM
                ? 'Zoom in to enable panning'
                : 'Pan (drag to move the canvas)'
            }
          >
            <FontAwesomeIcon icon={faHand} />
          </button>
        </div>

        {/* Canvas */}
        <div className="skc__canvas-wrap" ref={wrapRef}>
          <canvas
            ref={canvasRef}
            width={800}
            height={560}
            className={`skc__canvas ${tool === 'pan' || (spaceHeld && zoom > 1) ? 'skc__canvas--pan' : ''}`}
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          {showHint && !isLocked && (
            <div className="skc__zoom-hint" role="note">
              <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
              {isTouchDevice ? (
                <span>Pinch to zoom · drag with two fingers (or use ✋) to pan</span>
              ) : (
                <span>
                  <kbd>Ctrl</kbd>+scroll to zoom · hold <kbd>Space</kbd> or use ✋ to pan
                </span>
              )}
              <button
                className="skc__zoom-hint-close"
                onClick={() => setShowHint(false)}
                aria-label="Dismiss hint"
                title="Dismiss"
              >
                ×
              </button>
            </div>
          )}
          {submitted && (
            <div className="skc__submitted-overlay">
              <span>✅ Sketch submitted!</span>
              <p>Waiting for other players...</p>
            </div>
          )}
          {timerExpired && !submitted && (
            <div className="skc__submitted-overlay">
              <span>⏰ Time's up!</span>
              <p>{canvasModifiedRef.current ? 'Your sketch was auto-submitted.' : 'No sketch was submitted.'}</p>
            </div>
          )}
        </div>

        {/* Color palette + Submit */}
        <div className="skc__bottom-bar">
          <div className="skc__palette">
            {COLORS.map((c) => (
              <button
                key={c}
                className={`skc__color-btn ${color === c && tool === 'brush' ? 'skc__color-btn--active' : ''}`}
                style={{ background: c }}
                onClick={() => {
                  setColor(c);
                  setTool('brush');
                }}
                title={c}
              />
            ))}
          </div>
          <button
            className="skc__submit-btn"
            onClick={handleSubmit}
            disabled={isLocked}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
            {submitted ? 'Submitted' : timerExpired ? "Time's Up" : 'Submit Sketch'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SketcherCanvas;
